import SuperFetch from 'library/helpers/superFetch';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { watchActivity } from '../_shared/activity';
import { selectSiteByOrgAndSiteID } from './selectors';
import { sitesActions } from './slice';

export function* getSites() {
	try {
		// Get the orgID from the selected profile
		const { orgID } = yield select((state) => state.Auth.selectedProfile);

		// Get sites from the current organisation.
		const sites = yield call(() => SuperFetch.get('/sites'));
		if (sites.statusCode === 200) {
			yield put(sitesActions.loadSitesSuccess({ orgID, data: sites.data }));
		} else {
			throw Error(sites.error.message || sites.error || 'Failed to load sites.');
		}
	} catch (error) {
		yield put(sitesActions.loadSitesError(error));
	}
}

export function* getSiteAddress(action) {
	try {
		//
		const { siteID } = action.payload;

		// Get the orgID from the selected profile
		const { orgID } = yield select((state) => state.Auth.selectedProfile);

		// Get the address of the selected site.
		const address = yield call(() => SuperFetch.get(`/sites/${siteID}/address`));

		if (address.statusCode === 200) {
			yield put(sitesActions.loadSiteAddressSuccess({ orgID, siteID, data: address.data }));
		} else {
			throw Error(address.error.message || address.error || 'Failed to load address.');
		}
	} catch (error) {
		yield put(sitesActions.loadSiteAddressError(error));
	}
}

export function* createSite(action) {
	try {
		const { data } = action.payload;

		// ONE atomic request: the site, its address and its map are created together server-side in a
		// single transaction (POST /sites accepts nested `address` + `map`). This replaces the old
		// create-site → create-address → create-map orchestration, which was not atomic — an invalid
		// address left an orphaned site behind. entityID/entityTypeID are set by the backend.
		const payload = { name: data.name };
		if (data.note) payload.note = data.note;

		// Address — sent only if the user entered anything; partial is allowed (all backend fields
		// are optional). Undefined fields are dropped by JSON serialization.
		const address = {
			addressLine1: data.addressLine1,
			addressLine2: data.addressLine2,
			suburb: data.suburb,
			state: data.state,
			country: data.country,
			postCode: data.postCode,
			latitude: data.latitude,
			longitude: data.longitude,
			fullAddress: data.fullAddress
		};
		const hasAnyAddress =
			address.addressLine1 ||
			address.addressLine2 ||
			address.suburb ||
			address.state ||
			address.country ||
			address.postCode ||
			address.fullAddress;
		if (hasAnyAddress) payload.address = address;

		/*
		 * The map is sent ONLY when there is a real image. A space with none leaves `MapID` NULL —
		 * the same "no map" state a media delete produces, so the UI already handles it — and the
		 * first real upload creates the IndoorMap row and links it (routesV5/media/query.js).
		 *
		 * This used to generate a blank white 100x100 PNG in the browser and upload it to Firebase,
		 * purely to satisfy `IndoorMap.Url` being NOT NULL: one junk object in storage per space ever
		 * created, and the last upload the dashboard did outside the broker.
		 */
		if (data.url) payload.map = { note: data.note, url: data.url };

		const createdSite = yield call(() => SuperFetch.post(`/sites`, payload));
		if (createdSite.statusCode === 200 && createdSite.data?.siteID) {
			Notification('success', 'Site created successfully.');
			// The id travels — step 2 of the Add site modal uploads the floor plan against it, and
			// the media broker cannot be addressed before the row it authorises against exists.
			yield put(sitesActions.createSiteSuccess({ siteID: createdSite.data.siteID }));
		} else {
			throw Error(createdSite.error?.message || 'Failed to create site.');
		}
	} catch (error) {
		yield put(sitesActions.createSiteError(error));
		Notification('error', error.message || 'Failed to create site.');
	}
}

export function* afterCreateSiteSuccess() {
	yield put(sitesActions.loadSites()); // <- Refresh sites list
}

export function* updateSite(action) {
	try {
		const { siteID, data } = action.payload;

		// Get the previous site data to compare changes — we still diff so an unchanged address/map
		// isn't rewritten, but everything now ships in ONE atomic PATCH. The backend UPSERTS the
		// address/map (updates the existing row or inserts one) and resolves the existing ids itself,
		// so the client no longer sends addressID/mapID or entityID/entityTypeID.
		const previousData = yield select(selectSiteByOrgAndSiteID, siteID);

		const payload = { name: data.name };
		if (data.note !== undefined) payload.note = data.note;

		// Address — include only if it changed AND the user has any address field (partial allowed).
		const addressChanged = !isEqual(
			pick(data, [
				'addressLine1',
				'addressLine2',
				'suburb',
				'state',
				'country',
				'postCode',
				'latitude',
				'longitude',
				'fullAddress'
			]),
			pick(previousData, [
				'addressLine1',
				'addressLine2',
				'suburb',
				'state',
				'country',
				'postCode',
				'latitude',
				'longitude',
				'fullAddress'
			])
		);
		if (addressChanged) {
			const address = {
				addressLine1: data.addressLine1,
				addressLine2: data.addressLine2,
				suburb: data.suburb,
				state: data.state,
				country: data.country,
				postCode: data.postCode,
				latitude: data.latitude,
				longitude: data.longitude,
				fullAddress: data.fullAddress
			};
			const hasAnyAddress =
				address.addressLine1 ||
				address.addressLine2 ||
				address.suburb ||
				address.state ||
				address.country ||
				address.postCode ||
				address.fullAddress;
			if (hasAnyAddress) payload.address = address;
		}

		// Map — include only if it changed and there is a url (IndoorMap.Url is NOT NULL, so the
		// backend requires it whenever a map block is sent).
		const mapChanged = !isEqual(pick(data, ['note', 'url']), pick(previousData, ['note', 'url']));
		if (mapChanged && data.url) {
			// name + dimensions omitted — the backend mirrors the site name onto the floor plan and
			// owns the map dimensions; the client only sends the image url (and optional note).
			payload.map = {
				note: data.note,
				url: data.url
			};
		}

		const updated = yield call(() => SuperFetch.patch(`/sites/${siteID}`, payload));
		if (updated.statusCode !== 200) throw Error(updated.error?.message || 'Failed to update site.');

		Notification('success', 'Site updated successfully.');
		yield put(sitesActions.updateSiteSuccess());
	} catch (error) {
		yield put(sitesActions.updateSiteError(error));
		Notification('error', error.message || 'Failed to update site.');
	}
}

export function* afterUpdateSiteSuccess() {
	yield put(sitesActions.loadSites());
}

// ✅ Handle single or multiple site deletions via the bulk endpoint.
// The backend deletes each site independently — one failure doesn't abort the rest,
// so the response reports per-site results plus a { deleted, failed } summary.
export function* deleteSite(action) {
	try {
		const { siteIDs } = action.payload;

		// Bulk endpoint accepts at most 50 IDs per request
		const chunks = [];
		for (let i = 0; i < siteIDs.length; i += 50) chunks.push(siteIDs.slice(i, i + 50));

		const responses = yield all(
			chunks.map((chunk) => call(() => SuperFetch.delete('/sites/bulk', { siteIDs: chunk })))
		);

		let deleted = 0;
		let failed = 0;
		responses.forEach((res) => {
			if (![200, 204].includes(res.statusCode)) {
				throw Error(res.error?.message || res.error || 'Failed to delete site(s).');
			}
			const summary = res.data?.summary || res.summary || {};
			deleted += summary.deleted || 0;
			failed += summary.failed || 0;
		});

		if (deleted === 0 && failed > 0) {
			throw Error(`Failed to delete ${failed} site${failed > 1 ? 's' : ''}.`);
		}

		yield put(sitesActions.deleteSiteSuccess());

		if (failed > 0) {
			Notification('warning', 'Some sites were not deleted', `${deleted} deleted, ${failed} failed.`);
		} else {
			Notification(
				'success',
				deleted > 1 ? 'Sites deleted' : 'Site deleted',
				deleted > 1 ? `${deleted} sites have been permanently deleted.` : 'Site has been permanently deleted.'
			);
		}
	} catch (error) {
		yield put(sitesActions.deleteSiteError(error));
		Notification('error', 'Delete failed', error.message || 'Failed to delete site.');
	}
}

export function* afterDeleteSiteSuccess() {
	yield put(sitesActions.loadSites());
}

export function* getSiteMaps({ payload }) {
	try {
		// Get the orgID from the selected profile
		const { orgID } = yield select((state) => state.Auth.selectedProfile);

		// Get the siteID from the payload
		const { siteID } = payload;

		// Get the map of the selected site.
		const map = yield call(() => SuperFetch.get(`/sites/${siteID}/maps`));

		if (map.statusCode === 200) {
			yield put(sitesActions.loadSiteMapSuccess({ orgID, siteID, data: map.data }));
		} else {
			throw Error(map.error.message || map.error || 'Failed to load floor plan.');
		}
	} catch (error) {
		yield put(sitesActions.loadSiteMapError(error));
	}
}

export default function* rootSaga() {
	yield takeLatest(sitesActions.loadSites, getSites);
	yield takeLatest(sitesActions.loadSiteAddress, getSiteAddress);
	yield takeLatest(sitesActions.createSite, createSite);
	yield takeLatest(sitesActions.createSiteSuccess, afterCreateSiteSuccess);
	yield takeLatest(sitesActions.updateSite, updateSite);
	yield takeLatest(sitesActions.updateSiteSuccess, afterUpdateSiteSuccess);
	yield takeLatest(sitesActions.deleteSite, deleteSite);
	yield takeLatest(sitesActions.deleteSiteSuccess, afterDeleteSiteSuccess);
	yield takeLatest(sitesActions.loadSiteMap, getSiteMaps);
	yield* watchActivity(sitesActions);
}
